import { useCallback, useEffect, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import {
  focusEsignPopup,
  isEsignPopupActive,
  subscribeEsignPopupActive,
} from '@auth/utils/requestEsignReauthentication'

/**
 * Blocks interaction with the parent GCIS window while the Keycloak e-sign
 * popup window is open, and offers a user-gesture way to bring that popup forward.
 *
 * Visibility follows isEsignPopupActive, which clears as soon as the retained
 * popup reports closed — not when the full signinPopup() promise settles.
 *
 * Do not auto-focus the popup when the parent window gains focus: that fights
 * the OS minimize control and pulls the popup back on every minimize.
 */
export function EsignPopupOverlay() {
  const [open, setOpen] = useState(isEsignPopupActive)

  useEffect(() => subscribeEsignPopupActive(setOpen), [])

  const handleReturnToEsign = useCallback(() => {
    focusEsignPopup()
  }, [])

  if (!open) {
    return null
  }

  return (
    <Dialog
      open
      disableEscapeKeyDown
      hideBackdrop={false}
      disableRestoreFocus
      // Keep the overlay open; visibility is driven by popup-active state, not Dialog close.
      onClose={handleReturnToEsign}
      onBackdropClick={(event) => {
        event.stopPropagation()
        handleReturnToEsign()
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        },
        zIndex: 9998,
      }}
      data-testid="esign-popup-overlay"
    >
      <DialogTitle>Complete e-sign authentication</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Authentication is open in another window. Finish signing there, or use the button below to bring that window
          back to the front.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReturnToEsign} color="primary" variant="contained" autoFocus>
          Return to eSign
        </Button>
      </DialogActions>
    </Dialog>
  )
}
